import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { FullScreenError, FullScreenLoader } from "@/components/ui/FullScreenState";
import { useSession } from "@/features/auth/useSession";
import { useCharacter } from "@/features/character/useCharacter";

const CREATE_CHARACTER_PATH = "/character/create";

/** Only same-site paths are allowed as post-login destinations (no open redirects). */
function safeReturnPath(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : "/";
}

type GateQuery = {
  isPending: boolean;
  isError: boolean;
  refetch: () => unknown;
};

/** Loader → error with retry → children. Shared by every guard. */
function QueryGate({
  query,
  loadingLabel,
  children,
}: {
  query: GateQuery;
  loadingLabel: string;
  children: () => ReactNode;
}) {
  if (query.isPending) {
    return <FullScreenLoader label={loadingLabel} />;
  }
  if (query.isError) {
    return (
      <FullScreenError
        title="Can't reach Coal City"
        message="The servers aren't responding. Check your connection, then try again."
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <>{children()}</>;
}

/** Signed-out visitors are sent to /login and returned here afterwards. */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();
  return (
    <QueryGate query={session} loadingLabel="Restoring your session…">
      {() =>
        session.data ? <Outlet /> : <Navigate to="/login" replace state={{ from: location.pathname }} />
      }
    </QueryGate>
  );
}

/** Signed-in players skip the login/register screens. */
export function RequireGuest() {
  const session = useSession();
  const location = useLocation();
  return (
    <QueryGate query={session} loadingLabel="Restoring your session…">
      {() => (session.data ? <Navigate to={safeReturnPath(location.state)} replace /> : <Outlet />)}
    </QueryGate>
  );
}

/** Players without a character are sent to the studio first. Use inside RequireAuth. */
export function RequireCharacter() {
  const character = useCharacter();
  return (
    <QueryGate query={character} loadingLabel="Loading your citizen…">
      {() => (character.data ? <Outlet /> : <Navigate to={CREATE_CHARACTER_PATH} replace />)}
    </QueryGate>
  );
}

/** The studio is only for players who don't have a character yet. Use inside RequireAuth. */
export function RequireNoCharacter() {
  const character = useCharacter();
  return (
    <QueryGate query={character} loadingLabel="Loading your citizen…">
      {() => (character.data ? <Navigate to="/" replace /> : <Outlet />)}
    </QueryGate>
  );
}